/**
 * @fileoverview Load, search, and page the B2B events list.
 * @module pages/b2bEvents/useB2BEventsPage
 */

import { use, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../context/AppContext";
import {
  getB2BEventParticipatingCompanies,
  getData,
  showEventCreatedSuccess,
} from "../../logic/b2bEvents";
import { buildBuyerScheduleRowsFromParticipatingCompanies } from "../../utils/b2bEventVirtualLineSchedules";
import {
  canManageB2BEventStaffActions,
  canManageB2BEvents,
} from "../../utils/accessControl";
import useRequireAccess from "../../hooks/useRequireAccess";
import { logger, sanitizeError } from "../../utils/logger.js";
import { DEFAULT_RESULTS_LIMIT } from "../../utils/resultsLimitOptions.js";
import {
  eventsDeviceName,
  eventsSearchBarWidth,
  readEventsViewportFlags,
} from "./b2bEventsLayout";

const FIRST_PAGE = 1;

/**
 * @returns {Object}
 */
const useB2BEventsData = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions, permissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageB2BEvents);
  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [user, setUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const events = await getData(
        localStorage.getItem("userId"),
        changePermissions,
        navigate,
        setUser,
      );
      setEventsData(events);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  }, [changePermissions, navigate]);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const initializeData = async () => {
      try {
        await fetchData();
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    initializeData();
    // Reload only when access changes, matching the previous effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  useEffect(() => {
    if (location.state?.eventCreated) {
      showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  return {
    navigate,
    permissions,
    allowed,
    eventsData,
    isLoading,
    searchQuery,
    setSearchQuery,
    resultsLimit,
    setResultsLimit,
    currentPage,
    setCurrentPage,
    user,
    showSuccessMessage,
    isSlidingUp,
  };
};

/**
 * Buyer schedule dialog for the signed-in executive.
 * @param {Object} page
 * @returns {Object}
 */
const useBuyerSchedulesModal = (page) => {
  const [buyerSchedulesModalOpen, setBuyerSchedulesModalOpen] = useState(false);
  const [buyerSchedulesModalLoading, setBuyerSchedulesModalLoading] =
    useState(false);
  const [buyerSchedulesModalEvent, setBuyerSchedulesModalEvent] =
    useState(null);
  const [buyerSchedulesModalRows, setBuyerSchedulesModalRows] = useState([]);

  const closeBuyerSchedulesModal = useCallback(() => {
    setBuyerSchedulesModalOpen(false);
    setBuyerSchedulesModalEvent(null);
    setBuyerSchedulesModalRows([]);
    setBuyerSchedulesModalLoading(false);
  }, []);

  const openBuyerSchedulesModal = useCallback(
    async (event) => {
      const uid = localStorage.getItem("userId");
      if (!uid || !event?._id || !page.user?._id) {
        return;
      }
      setBuyerSchedulesModalEvent(event);
      setBuyerSchedulesModalOpen(true);
      setBuyerSchedulesModalLoading(true);
      setBuyerSchedulesModalRows([]);
      try {
        const list = await getB2BEventParticipatingCompanies(
          event._id,
          page.navigate,
        );
        const rows = buildBuyerScheduleRowsFromParticipatingCompanies(
          Array.isArray(list) ? list : [],
          String(page.user._id),
        );
        setBuyerSchedulesModalRows(rows);
      } finally {
        setBuyerSchedulesModalLoading(false);
      }
    },
    [page.navigate, page.user],
  );

  const handleViewVirtualLineFromBuyerModal = useCallback(
    (row) => {
      if (!buyerSchedulesModalEvent?._id) {
        return;
      }
      page.navigate("/b2bEventUsersList", {
        state: {
          virtualLinePreset: {
            eventId: String(buyerSchedulesModalEvent._id),
            buyerUserId: row.buyerUserId,
            fecha: row.fecha,
            horaInicio: row.horaInicio,
            horaFin: row.horaFin,
            eventNombre: buyerSchedulesModalEvent.nombre,
            buyerName: row.buyerName,
            companyName: row.nombreComercial,
            returnPath: "/b2bEvents",
          },
        },
      });
      closeBuyerSchedulesModal();
    },
    [buyerSchedulesModalEvent, closeBuyerSchedulesModal, page],
  );

  return {
    buyerSchedulesModalOpen,
    buyerSchedulesModalLoading,
    buyerSchedulesModalRows,
    closeBuyerSchedulesModal,
    openBuyerSchedulesModal,
    handleViewVirtualLineFromBuyerModal,
  };
};

/**
 * B2B events catalog: search, paging, staff actions, and the buyer schedule dialog.
 * @returns {Object}
 */
export function useB2BEventsPage() {
  const { t } = useTranslation();
  const page = useB2BEventsData();
  const schedules = useBuyerSchedulesModal(page);
  const viewport = readEventsViewportFlags(window.innerWidth);
  const canManageStaff = canManageB2BEventStaffActions(page.user, page.permissions);

  const handleSearch = useCallback(
    (event) => {
      page.setSearchQuery(event.target.value);
    },
    [page],
  );

  const handleResultsLimitChange = useCallback(
    (selectedOption) => {
      page.setResultsLimit(selectedOption.value);
      page.setCurrentPage(FIRST_PAGE);
    },
    [page],
  );

  const handlePageChange = useCallback(
    (direction) => {
      const nextPageLimit = Math.ceil(page.eventsData.length / page.resultsLimit);
      if (direction === "next" && page.currentPage < nextPageLimit) {
        page.setCurrentPage((prevPage) => prevPage + 1);
      } else if (direction === "prev" && page.currentPage > FIRST_PAGE) {
        page.setCurrentPage((prevPage) => prevPage - 1);
      } else {
        // Unsupported direction or already at bounds. No action required.
      }
    },
    [page],
  );

  const openCreateForm = useCallback(() => {
    page.navigate("/b2bEventForm", { state: { isEditMode: false } });
  }, [page]);

  const filteredEventsList = page.eventsData?.filter((event) =>
    event.nombre
      ? event.nombre.toLowerCase().includes(page.searchQuery.toLowerCase())
      : false,
  );
  const paginatedEventsList = filteredEventsList.slice(
    (page.currentPage - 1) * page.resultsLimit,
    page.currentPage * page.resultsLimit,
  );

  return {
    t,
    ...page,
    ...schedules,
    viewport,
    canManageStaff,
    showVirtualLineCompaniesButton: canManageStaff,
    searchBarWidth: eventsSearchBarWidth(eventsDeviceName(viewport)),
    handleSearch,
    handleResultsLimitChange,
    handlePageChange,
    openCreateForm,
    filteredEventsList,
    paginatedEventsList,
    totalPages: Math.ceil(filteredEventsList.length / page.resultsLimit),
  };
}
